/**
 * Places workshops on the week grid, whose columns are whole hours ("08:00", "09:00", …).
 * A workshop may start off the hour (17:15–19:15), so it takes every hour column it
 * overlaps, the same hours whose stôl/kruh slots it blocks: 17:00, 18:00 and 19:00.
 * Matching only the exact start time used to leave such a workshop out of the grid.
 */

export interface GridWorkshop {
  start_time: string;
  end_time: string;
}

export interface PlacedWorkshop<T> {
  ws: T;
  span: number;
}

/** "17:00" → "18:00": a column covers one hour. */
function plusHour(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  return String(h + 1).padStart(2, "0") + ":" + String(m).padStart(2, "0");
}

/**
 * Returns where each workshop starts (column → workshop and its colSpan) and which columns
 * the workshops cover. Workshops never overlap in time, but two can share an hour column
 * (17:15–19:15 and 19:30–20:30 both touch 19:00); the earlier one keeps it and the later
 * one starts at its next free column. A workshop with no free column is left to the day view.
 */
export function placeWorkshops<T extends GridWorkshop>(
  workshops: T[],
  columns: string[],
): { starts: Map<string, PlacedWorkshop<T>>; covered: Set<string> } {
  const starts = new Map<string, PlacedWorkshop<T>>();
  const covered = new Set<string>();
  const byStart = [...workshops].sort((a, b) => a.start_time.localeCompare(b.start_time));

  for (const ws of byStart) {
    const start = ws.start_time.slice(0, 5);
    const end = ws.end_time.slice(0, 5);
    const first = columns.findIndex((c) => c < end && plusHour(c) > start && !covered.has(c));
    if (first < 0) continue;

    let span = 0;
    while (first + span < columns.length) {
      const c = columns[first + span];
      if (!(c < end && plusHour(c) > start) || covered.has(c)) break;
      covered.add(c);
      span++;
    }
    starts.set(columns[first], { ws, span });
  }
  return { starts, covered };
}
